import { useEffect, useRef, useState } from "react";
import { Calendar, Download, ChevronDown, Bell, User, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../hooks/useAuthStore";
import styles from "../../style/admin.module.css";

function AdminHeader({
  title = "Dashboard Overview",
  subtitle,
  showControls = true,
  showTitle = true,
  onNotificationsClick,
  notificationCount = 0,
  notificationsOpen = false,
  onMenuClick,
}) {
  const navigate = useNavigate();
  const { userName, actions } = useAuthStore();
  
  const [selectedDate, setSelectedDate] = useState("Dec 2025");
  const [dateOpen, setDateOpen] = useState(false);
  const [dateQuery, setDateQuery] = useState("");
  const dateRef = useRef(null);
  
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  
  const dateOptions = ["Dec 2025", "Nov 2025", "Oct 2025", "Sep 2025"];
  const filteredDateOptions = dateQuery.trim()
    ? dateOptions.filter((option) =>
        option.toLowerCase().includes(dateQuery.trim().toLowerCase())
      )
    : dateOptions;

  useEffect(() => {
    const handleOutside = (event) => {
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setDateOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleDownloadReport = () => {
    // TODO: Implement download report functionality
    console.log("Download report clicked");
  };

  const handleLogout = async () => {
    setIsProfileOpen(false);
    await actions.logout();
    navigate("/auth/admin/login");
  };

  return (
    <header className={styles.adminHeader}>
      <div className={`${styles.headerContent} ${!showControls ? styles.headerNoControls : ""}`}>
        <div className={styles.headerLeft}>
          {onMenuClick && (
            <button
              type="button"
              className={styles.mobileMenuButtonInline}
              onClick={onMenuClick}
              aria-label="Open menu"
            >
              <Menu className={styles.menuIcon} />
            </button>
          )}
          {showTitle ? (
            <>
              <h1 className={styles.headerTitle}>{title}</h1>
              {subtitle && <p className={styles.headerSubtitle}>{subtitle}</p>}
            </>
          ) : null}
        </div>

        <div className={styles.headerRight}>
          {showControls ? (
            <>
              <div className={styles.dateSelector} ref={dateRef}>
                <Calendar className={styles.calendarIcon} />
                <button
                  type="button"
                  className={styles.dateButton}
                  onClick={() => setDateOpen((prev) => !prev)}
                >
                  <span>{selectedDate}</span>
                  <ChevronDown
                    className={`${styles.dateChevron} ${dateOpen ? styles.dateChevronOpen : ""}`}
                  />
                </button>
                {dateOpen && (
                  <div className={styles.dateMenu}>
                    <div className={styles.dateSearch}>
                      <input
                        type="text"
                        value={dateQuery}
                        onChange={(event) => setDateQuery(event.target.value)}
                        placeholder="Search month"
                        className={styles.dateSearchInput}
                      />
                    </div>
                    <div className={styles.dateOptions}>
                      {filteredDateOptions.length ? (
                        filteredDateOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              setSelectedDate(option);
                              setDateOpen(false);
                              setDateQuery("");
                            }}
                            className={`${styles.dateOption} ${
                              option === selectedDate ? styles.dateOptionActive : ""
                            }`}
                          >
                            {option}
                          </button>
                        ))
                      ) : (
                        <p className={styles.dateEmpty}>No results found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleDownloadReport}
                className={styles.downloadButton}
              >
                <Download className={styles.downloadIcon} />
                <span>Download Report</span>
              </button>
            </>
          ) : null}

          <button
            type="button"
            onClick={onNotificationsClick}
            aria-label="Notifications"
            aria-expanded={notificationsOpen}
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#D6DCE4] bg-white text-[#4B5565] transition hover:bg-gray-50"
          >
            <Bell size={18} strokeWidth={1.9} />
            {notificationCount > 0 ? (
              <span className="absolute right-0 top-0 inline-flex h-4 min-w-4 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-[#F59E0B] px-1 text-[10px] font-semibold text-white">
                {notificationCount}
              </span>
            ) : null}
          </button>
          <div className="relative" ref={profileRef}>
            <button
              type="button"
              onClick={() => setIsProfileOpen((prev) => !prev)}
              aria-haspopup="true"
              aria-expanded={isProfileOpen}
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#D6DCE4] bg-white text-[#4B5565] transition hover:bg-gray-50 cursor-pointer"
              aria-label="Profile"
            >
              <User size={18} strokeWidth={1.9} />
            </button>
            {isProfileOpen && (
              <div
                className="space-y-2 absolute right-0 mt-2 py-3 px-3 w-48 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg ring-1 ring-black/5 z-[1050]"
              >
                <div className="border-b border-gray-100 pb-2 px-2 text-sm font-semibold text-gray-700 truncate">
                  {userName || "Admin User"}
                </div>
                <button
                  type="button"
                  className="w-full text-left px-2 py-2 text-sm rounded-lg font-medium text-gray-800 transition hover:bg-[#FAF7FA] hover:text-[#61013B] cursor-pointer"
                  onClick={() => {
                    setIsProfileOpen(false);
                    navigate("/admin/settings");
                  }}
                >
                  Settings
                </button>
                <button
                  type="button"
                  className="w-full text-left px-2 py-2 text-sm rounded-lg font-medium text-red-600 transition hover:bg-red-50 cursor-pointer"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default AdminHeader;
